import { useContext, useMemo, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";

type CarrierPriceWithKey = CarrierPrice & { uniqueKey: string };

type UseRoutingGuideToggleResult = {
	isGuideActive: boolean;
	showingHidden: boolean;
	trigger: string | null;
	filteredCarriers: CarrierPriceWithKey[];
	hiddenCarrierCount: number;
	handleToggleGuide: () => void;
	handleToggleHiddenCarriers: () => void;
};

export function useRoutingGuideToggle(
	carrierPrices: CarrierPrice[],
): UseRoutingGuideToggleResult {
	const { quoteState } = useContext(QuoteDetailsContext);

	const hasAnyRoutingGuideCarrier = useMemo(
		() => carrierPrices.some((c) => c.routingGuideApplied === true),
		[carrierPrices],
	);

	const guideAvailable =
		quoteState.hasRoutingGuideMatch || hasAnyRoutingGuideCarrier;

	const [userOverride, setUserOverride] = useState<boolean | undefined>(
		undefined,
	);

	const guideEnabled = userOverride ?? guideAvailable;

	const [showingHidden, setShowingHidden] = useState(false);
	const [trigger, setTrigger] = useState<string | null>(null);

	const carriersWithKeys = useMemo(() => {
		const seen = new Map<string, number>();
		return carrierPrices.map((carrier) => {
			const base = `${carrier.carrierId}-${carrier.carrierPriceTypeId}-${carrier.serviceLevelId}`;
			const count = seen.get(base) ?? 0;
			seen.set(base, count + 1);
			return { ...carrier, uniqueKey: `${base}-${count}` };
		});
	}, [carrierPrices]);

	const filteredCarriers = useMemo(
		() =>
			carriersWithKeys.filter((carrier) =>
				guideEnabled && !showingHidden
					? carrier.routingGuideApplied === true || carrier.isAdHoc === true
					: true,
			),
		[carriersWithKeys, guideEnabled, showingHidden],
	);

	const hiddenCarrierCount = useMemo(
		() =>
			carrierPrices.filter(
				(carrier) =>
					carrier.routingGuideApplied !== true && carrier.isAdHoc !== true,
			).length,
		[carrierPrices],
	);

	const handleToggleGuide = () => {
		const next = !guideEnabled;
		setUserOverride(next === guideAvailable ? undefined : next);
		setShowingHidden(false);
		setTrigger("toggle");
	};

	const handleToggleHiddenCarriers = () => {
		setShowingHidden((prev) => !prev);
		setTrigger("viewHidden");
	};

	return {
		isGuideActive: guideEnabled,
		showingHidden,
		trigger,
		filteredCarriers,
		hiddenCarrierCount,
		handleToggleGuide,
		handleToggleHiddenCarriers,
	};
}
